import React from "react";
import { useForm, Controller } from "react-hook-form";
import Typography from "@mui/material/Typography";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import PersonOutline from "@mui/icons-material/PersonOutline";
import moment from "moment-timezone";
import LockIcon from "@mui/icons-material/Lock";
import Button from "@mui/material/Button";
import { enUS, enAU } from "date-fns/locale";
import { SaveLoader } from "@/_components";
import PropTypes from "prop-types";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    [theme.breakpoints.up("md")]: { padding: theme.spacing(4) },
    [theme.breakpoints.only("sm")]: { padding: theme.spacing(2) },
    [theme.breakpoints.down("sm")]: { paddingTop: theme.spacing(2) },
  },
  paper: {
    maxWidth: 400,
    [theme.breakpoints.down("md")]: { maxWidth: "100%" },
    // [theme.breakpoints.up("md")]: { padding: theme.spacing(4) },
    // [theme.breakpoints.down("md")]: { padding: theme.spacing(2) },
  },
  customIconUnauth: {
    color: "#1c355e",
    height: 200,
    width: 200,
    opacity: 0.1,
  },
  customTypoUnauth: {
    textAlign: "center",
    zIndex: 10,
    fontSize: 20,
    marginTop: -180,
  },
  paperNoData: {
    padding: theme.spacing(2),
    textAlign: "left",
    height: 320,
    width: 320,
  },
  paperUpdate: {
    padding: theme.spacing(2),
    textAlign: "left",
    height: 320,
    width: 320,
  },
  customTypoUpdate: {
    textAlign: "center",
    zIndex: 10,
    fontSize: 20,
    marginTop: -150,
  },
  cardHeader: {
    variant: "h1",
    fontSize: 20,
    fontWeight: 600,
    textAlign: "center",
  },
}));

const IdentityVerificationCard = ({
  handleIdentityVerification,
  submitting,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const locale = countryCode === "USA" ? enUS : enAU;
  const countryCode = sessionStorage.getItem("LSCountryCode");

  const {
    register,
    errors,
    handleSubmit,
    control,
    // setError, setValue, clearErrors
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      lastName: null,
      dob: null,
    },
  });

  const onSubmit = (data) => {
    let formattedDate = new Intl.DateTimeFormat("en-US")
      .format(new Date(data.dob))
      .toString();

    // let obj = {
    //   LastName: data.lastName,
    //   DOB: formattedDate,
    //   page: "My Account Sign Now",
    //   //   token: token.current,
    // };

    let obj = {
      LastName: data.lastName,
      DOB: formattedDate,
      page: "My Account Auto Pay",
      // Token: token.current,
    };

    handleIdentityVerification(obj);
  };

  return (
    <>
      <Container sx={{marginTop:"30px"}}>
        {/* <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Grid item>
                        <img
                            src={"./src/_images/LogoLimited.png"}
                            alt="LifeSafer"
                        />
                        <Typography
                            variant="h5"
                            sx={{
                                padding: "1rem",
                                color: "#1c355e",
                                fontWeight: "bold",
                                textAlign: "center",
                            }}
                        >
                            {t("account.secureIdUpload")}
                        </Typography>
                    </Grid> */}

        <Grid container direction="row" justifyContent="center" >
          <Grid item>
            <Paper className={classes.paper} elevation={2}>
              <Grid container style={{ paddingBottom: 20 }}>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textAlign: "left",
                      alignItems: "center",
                      display: "flex",
                      gap: "2px",
                      padding: 2,
                    }}
                  >
                    <LockIcon
                      sx={{
                        color: "#1c355e",
                      }}
                    />
                    {t("registration.identityVerification")}
                  </Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} sx={{ padding: 2 }}>
                  <form
                    className={classes.form}
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <TextField
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      id="lastName"
                      label={t("profile.lName")}
                      name="lastName"
                      // disabled={submitting}
                      error={!!errors.lastName}
                      helperText={
                        errors.lastName ? errors.lastName.message : ""
                      }
                      inputRef={register({
                        required: {
                          value: true,
                          message: t("profile.lastNameRequired"),
                        },
                      })}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutline className={classes.inputIco} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={locale}
                    >
                      <Controller
                        control={control}
                        name="dob"
                        rules={{
                          required: {
                            value: true,
                            message: t("profile.dobRequired"),
                          },
                          validate: (value) => {
                            const dateValue = new Date(value);
                            if (isNaN(dateValue)) {
                              return t("profile.dobInvalid");
                            }
                            const minDate = moment()
                              .subtract(160, "years")
                              .toDate();
                            const maxDate = moment()
                              .subtract(14, "years")
                              .toDate();
                            if (dateValue < minDate || dateValue > maxDate) {
                              return t("profile.dobInvalid");
                            }
                          },
                        }}
                        render={(props) => (
                          <DesktopDatePicker
                            {...props}
                            id="dob"
                            label={t("profile.dobLabel")}
                           inputFormat={
                              countryCode === "USA" ? "MM/dd/yyyy" : "dd/MM/yyyy"
                            }
                            disableFuture
                            openTo="year"
                            views={["year", "month", "day"]}
                            // disabled={disableInputs}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                margin="normal"
                                size="small"
                                error={!!errors.dob}
                                helperText={
                                  errors.dob ? errors.dob.message : ""
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                          />
                        )}
                        size="small"
                      />
                    </LocalizationProvider>

                    <Grid
                      container
                      alignItems="center"
                      justifyContent="center"
                      direction="column"
                      mt={2}
                    >
                      <Grid item>
                        {submitting === false ? (
                          <Button
                            type="submit"
                            size="small"
                            variant="contained"
                            color="primary"
                            // disabled={submitting}
                            className={classes.submit}
                            // style={{ width: 240, height: 50 }}
                            style={{ height: 35 }}
                            // startIcon={<CheckIcon />}
                          >
                            {t("registration.verify")}
                          </Button>
                        ) : (
                          <SaveLoader />
                        )}
                      </Grid>
                    </Grid>
                  </form>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        {/* </Grid> */}
      </Container>
    </>
  );
};

IdentityVerificationCard.propTypes = {
  handleIdentityVerification: PropTypes.func,
  submitting: PropTypes.bool,
};
export default IdentityVerificationCard;